import { useState, useEffect } from 'react';

import { useToast } from './useToast';
import { apiOrchestrator } from 'services/apiService';
import { handleHttpError } from 'utils/errorHandler';

export type RecoveryFlow = 'email' | 'password' | null;

// LocalStorage key for tracking failed login attempts
const FAILED_ATTEMPTS_KEY = 'login_failed_attempts';
const FAILED_ATTEMPTS_TIMESTAMP_KEY = 'login_failed_attempts_timestamp';
const ATTEMPTS_RESET_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds

interface UseAccountRecoveryReturn {
  // State
  recoveryFlow: RecoveryFlow;
  showWhatsAppModal: boolean;
  showCodeValidationModal: boolean;
  showEmailRecoveredModal: boolean;
  showPasswordRecoveryModal: boolean;
  recoveredEmail: string | null;
  whatsappNumber: string | null;
  loading: boolean;
  failedAttempts: number;

  // Actions
  checkLoginError: (email: string) => Promise<void>;
  setShowWhatsAppModal: (show: boolean) => void;
  setShowCodeValidationModal: (show: boolean) => void;
  setShowEmailRecoveredModal: (show: boolean) => void;
  setShowPasswordRecoveryModal: (show: boolean) => void;
  setRecoveredEmail: (email: string | null) => void;
  setWhatsappNumber: (number: string | null) => void;
  resetRecoveryFlow: () => void;
  resetFailedAttempts: () => void;
}

/**
 * Get failed attempts from localStorage
 */
const getFailedAttempts = (): number => {
  try {
    const attempts = localStorage.getItem(FAILED_ATTEMPTS_KEY);
    const timestamp = localStorage.getItem(FAILED_ATTEMPTS_TIMESTAMP_KEY);

    if (!attempts || !timestamp) return 0;

    const lastAttemptTime = parseInt(timestamp, 10);
    const now = Date.now();

    // Reset if more than 5 minutes have passed
    if (now - lastAttemptTime > ATTEMPTS_RESET_TIMEOUT) {
      localStorage.removeItem(FAILED_ATTEMPTS_KEY);
      localStorage.removeItem(FAILED_ATTEMPTS_TIMESTAMP_KEY);
      return 0;
    }

    return parseInt(attempts, 10) || 0;
  } catch (error) {
    console.error('[AccountRecovery] Error reading failed attempts:', error);
    return 0;
  }
};

/**
 * Set failed attempts in localStorage
 */
const setFailedAttemptsStorage = (count: number): void => {
  try {
    localStorage.setItem(FAILED_ATTEMPTS_KEY, count.toString());
    localStorage.setItem(FAILED_ATTEMPTS_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('[AccountRecovery] Error setting failed attempts:', error);
  }
};

/**
 * Clear failed attempts from localStorage
 */
const clearFailedAttemptsStorage = (): void => {
  try {
    localStorage.removeItem(FAILED_ATTEMPTS_KEY);
    localStorage.removeItem(FAILED_ATTEMPTS_TIMESTAMP_KEY);
  } catch (error) {
    console.error('[AccountRecovery] Error clearing failed attempts:', error);
  }
};

/**
 * Hook to manage account recovery flow
 * Determines if login error is due to wrong email or wrong password
 * and routes user to appropriate recovery path
 *
 * Implements 2-attempt threshold:
 * - 1st failed attempt: Show generic error toast
 * - 2nd+ failed attempt: Show recovery options
 */
export const useAccountRecovery = (): UseAccountRecoveryReturn => {
  const { toastError } = useToast();
  const [recoveryFlow, setRecoveryFlow] = useState<RecoveryFlow>(null);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showCodeValidationModal, setShowCodeValidationModal] = useState(false);
  const [showEmailRecoveredModal, setShowEmailRecoveredModal] = useState(false);
  const [showPasswordRecoveryModal, setShowPasswordRecoveryModal] = useState(false);
  const [recoveredEmail, setRecoveredEmail] = useState<string | null>(null);
  const [whatsappNumber, setWhatsappNumber] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState<number>(getFailedAttempts());

  // Sync failed attempts from localStorage on mount and periodically
  useEffect(() => {
    const syncAttempts = () => {
      setFailedAttempts(getFailedAttempts());
    };

    // Check every minute if attempts should be reset
    const interval = setInterval(syncAttempts, 60000);

    return () => clearInterval(interval);
  }, []);

  /**
   * Check if email exists in database to determine recovery flow
   * @param email - Email address entered by user
   */
  const checkEmailExists = async (email: string): Promise<boolean> => {
    try {
      const response = await apiOrchestrator.post('/auth/check-email', { email });
      return response.data.exists;
    } catch (error) {
      console.error('[AccountRecovery] Error checking email:', error);
      handleHttpError(error);
      throw error;
    }
  };

  /**
   * Determine recovery flow based on login error
   *
   * 2-Attempt Threshold Logic:
   * - 1st failed attempt: Show generic error toast, increment counter
   * - 2nd+ failed attempt: Proceed with recovery flow
   *
   * If email exists → password error → trigger Cognito password recovery
   * If email doesn't exist → email error → trigger WhatsApp recovery
   */
  const checkLoginError = async (email: string): Promise<void> => {
    try {
      const currentAttempts = getFailedAttempts();
      const newAttempts = currentAttempts + 1;

      console.log('[AccountRecovery] Failed login attempt:', {
        currentAttempts,
        newAttempts,
        threshold: 2,
      });

      // Update attempts counter
      setFailedAttemptsStorage(newAttempts);
      setFailedAttempts(newAttempts);

      // First attempt: Show generic error toast only
      if (newAttempts < 2) {
        console.log('[AccountRecovery] First failed attempt - showing generic error');
        toastError('O seu e-mail e/ou senha foi digitado errado!');
        return;
      }

      // Second or more attempts: Proceed with recovery flow
      console.log('[AccountRecovery] Multiple failed attempts - initiating recovery flow');
      setLoading(true);

      console.log('[AccountRecovery] Checking email existence for:', email);
      const emailExists = await checkEmailExists(email);

      if (emailExists) {
        // Email exists in database → password is wrong
        console.log('[AccountRecovery] Email exists → Password error detected');
        setRecoveryFlow('password');
        setShowPasswordRecoveryModal(true);
      } else {
        // Email doesn't exist in database → email is wrong
        console.log('[AccountRecovery] Email not found → Email error detected');
        setRecoveryFlow('email');
        setShowWhatsAppModal(true);
      }
    } catch (error) {
      console.error('[AccountRecovery] Error determining recovery flow:', error);
      toastError('Erro ao verificar credenciais. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset failed attempts counter
   * Should be called on successful login
   */
  const resetFailedAttempts = () => {
    console.log('[AccountRecovery] Resetting failed attempts counter');
    clearFailedAttemptsStorage();
    setFailedAttempts(0);
  };

  /**
   * Reset recovery flow state
   */
  const resetRecoveryFlow = () => {
    setRecoveryFlow(null);
    setShowWhatsAppModal(false);
    setShowCodeValidationModal(false);
    setShowEmailRecoveredModal(false);
    setShowPasswordRecoveryModal(false);
    setRecoveredEmail(null);
    setWhatsappNumber(null);
  };

  return {
    // State
    recoveryFlow,
    showWhatsAppModal,
    showCodeValidationModal,
    showEmailRecoveredModal,
    showPasswordRecoveryModal,
    recoveredEmail,
    whatsappNumber,
    loading,
    failedAttempts,

    // Actions
    checkLoginError,
    setShowWhatsAppModal,
    setShowCodeValidationModal,
    setShowEmailRecoveredModal,
    setShowPasswordRecoveryModal,
    setRecoveredEmail,
    setWhatsappNumber,
    resetRecoveryFlow,
    resetFailedAttempts,
  };
};
